import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Card, Button, Form, Badge, Alert, Table, Spinner, Modal } from 'react-bootstrap';
import moment from 'moment';
import { shiftPreferenceAPI } from '../../services';
import { toast } from 'react-toastify';

// Lệnh sếp 23/10/2026: KHÔNG còn cron tự chốt bảng xếp hạng ca / tự trừ TLT điều kiện đăng ký ca.
// Admin duyệt ca xong, thấy ổn thì bấm "Chốt tuần": chốt bảng xếp hạng + cộng thưởng top 1/2/3, rồi
// trừ TLT những NV không đạt được tích chọn. Dòng đã trừ có nút "Hoàn TLT" (hoàn + Telegram
// "phục hồi tỷ lệ thưởng"). Dùng ở màn Quản lý ca làm việc (tab Lịch + tab Xếp hạng) và màn riêng.

// Thứ 2 của TUẦN SAU — cùng cách tính với tab Xếp hạng đóng góp ca.
const nextWeekMonday = () => moment().add(1, 'week').startOf('isoWeek').format('YYYY-MM-DD');

const STATUS_BADGE = {
  SE_TRU: { bg: 'danger', text: 'Không đạt' },
  DAT: { bg: 'success', text: 'Đạt' },
  MIEN_OFF: { bg: 'info', text: 'Miễn — xin off' },
  MIEN_TRAN_CA: { bg: 'info', text: 'Miễn — bị trần ca' },
  MIEN_NV_MOI: { bg: 'info', text: 'Miễn — NV mới/đổi chi nhánh' },
};

function addDays(ymd, delta) {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + delta, 12));
  return dt.toISOString().slice(0, 10);
}

function formatVn(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('vi-VN', {
    timeZone: 'Asia/Ho_Chi_Minh',
    hour: '2-digit',
    minute: '2-digit',
    day: '2-digit',
    month: '2-digit',
  });
}

function StatusCell({ row }) {
  if (row.status !== 'DA_CHOT') {
    const b = STATUS_BADGE[row.status] || { bg: 'secondary', text: row.status };
    return <Badge bg={b.bg}>{b.text}</Badge>;
  }
  const p = row.penalty;
  if (!p.penalized) return <Badge bg="secondary">Đã chốt — không trừ</Badge>;
  if (p.refunded_at) return <Badge bg="success">Đã hoàn {p.refunded_pct}%</Badge>;
  return <Badge bg="dark">Đã trừ {p.deducted_pct}%</Badge>;
}

function Metrics({ m }) {
  if (!m) return <span className="text-muted">—</span>;
  const cls = (ok) => (ok ? 'text-success' : 'text-danger fw-bold');
  return (
    <div className="small">
      <div className={cls(m.shifts_per_week >= m.min_shifts_per_week)}>
        Ca ưu tiên {m.shifts_per_week}/{m.min_shifts_per_week} ca/tuần
      </div>
      <div className={cls(m.registered_preferred >= m.required_preferred)}>
        Đăng ký {m.registered_preferred}/{m.required_preferred} ca ưu tiên
      </div>
      <div className={cls(m.weekend_count >= m.weekend_required)}>
        Cuối tuần {m.weekend_count}/{m.weekend_required}
      </div>
      <div className={cls(m.weekend_evening_count >= m.weekend_evening_required)}>
        Tối cuối tuần {m.weekend_evening_count}/{m.weekend_evening_required}
      </div>
    </div>
  );
}

function RefundModal({ row, onHide, onDone }) {
  // Mỗi dòng mở modal bằng key riêng (row.penalty.id) nên state tự về rỗng khi đổi dòng.
  const [lyDo, setLyDo] = useState('');
  const [daHoanTay, setDaHoanTay] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const res = await shiftPreferenceAPI.refundTltPenalty(row.penalty.id, {
        ly_do: lyDo.trim() || undefined,
        da_hoan_tay: daHoanTay,
      });
      toast.success(
        `Đã hoàn ${res?.refunded_pct ?? row.penalty.deducted_pct}% TLT cho ${row.full_name}` +
          (res?.telegram_notified ? ' — đã gửi Telegram' : ' — NV chưa liên kết Telegram'),
      );
      onDone();
    } catch (err) {
      toast.error(err?.message || 'Không hoàn được TLT');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      show={!!row}
      onHide={onHide}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>Hoàn TLT — {row?.full_name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-2">
          Hoàn lại <b>{row?.penalty?.deducted_pct}%</b> TLT đã trừ và gửi Telegram “Phục hồi tỷ lệ thưởng” cho nhân
          viên.
        </p>
        <div className="text-muted small mb-3">{row?.penalty?.reason}</div>
        <Form.Group className="mb-3">
          <Form.Label>Lý do (gửi kèm tin cho nhân viên, không bắt buộc)</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={lyDo}
            onChange={(e) => setLyDo(e.target.value)}
            placeholder="VD: Quản lý duyệt ca muộn, nhân viên không kịp đổi ca"
          />
        </Form.Group>
        <Form.Check
          type="checkbox"
          id="da-hoan-tay"
          checked={daHoanTay}
          onChange={(e) => setDaHoanTay(e.target.checked)}
          label="Đã sửa tay TLT cho nhân viên này rồi — chỉ đánh dấu đã hoàn và gửi thông báo, không cộng thêm"
        />
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={onHide}
          disabled={saving}
        >
          Huỷ
        </Button>
        <Button
          variant="success"
          onClick={submit}
          disabled={saving}
        >
          {saving ? (
            <Spinner
              size="sm"
              animation="border"
            />
          ) : (
            'Hoàn TLT'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default function WeekClosePanel({ branchId, initialWeekStart, onClosed }) {
  const [data, setData] = useState(null);
  const nextWeekStart = nextWeekMonday();
  // '' = tuần sau. Mở từ tab Xếp hạng thì bắt đầu ở đúng tuần đang xem ở tab đó.
  const [weekStart, setWeekStart] = useState(
    initialWeekStart && initialWeekStart !== nextWeekStart ? initialWeekStart : '',
  );
  const [chosen, setChosen] = useState(new Set());
  const [confirming, setConfirming] = useState(false);
  const [applying, setApplying] = useState(false);
  const [refundRow, setRefundRow] = useState(null);
  // Đang tải = khoá yêu cầu hiện tại chưa có kết quả (không setState đồng bộ trong effect).
  const [reloadTick, setReloadTick] = useState(0);
  const [loadedKey, setLoadedKey] = useState(null);
  const requestKey = `${branchId}|${weekStart || ''}|${reloadTick}`;
  const loading = !!branchId && loadedKey !== requestKey;
  const loadData = () => setReloadTick((t) => t + 1);

  useEffect(() => {
    if (!branchId) return undefined;
    let cancelled = false;
    shiftPreferenceAPI
      .getTltPenaltyPreview({ branch_id: branchId, week_start: weekStart || undefined })
      .then((res) => {
        if (cancelled) return;
        const payload = res?.rows ? res : res?.data;
        setData(payload);
        // Mặc định tích sẵn mọi NV không đạt — admin bỏ tích người muốn tha.
        setChosen(new Set((payload?.rows || []).filter((r) => r.status === 'SE_TRU').map((r) => r.employee_id)));
      })
      .catch((err) => {
        if (!cancelled) toast.error(err?.message || 'Không thể tải dữ liệu');
      })
      .finally(() => {
        if (!cancelled) setLoadedKey(requestKey);
      });
    return () => {
      cancelled = true;
    };
  }, [branchId, weekStart, requestKey]);

  const weekOptions = useMemo(() => {
    const opts = [
      { value: '', label: 'Tuần sau' },
      { value: addDays(nextWeekStart, -7), label: 'Tuần này' },
      { value: addDays(nextWeekStart, -14), label: 'Tuần trước' },
    ];
    if (weekStart && !opts.some((o) => o.value === weekStart)) {
      opts.push({ value: weekStart, label: `Tuần ${weekStart.slice(8, 10)}/${weekStart.slice(5, 7)}` });
    }
    return opts;
  }, [nextWeekStart, weekStart]);

  const rows = data?.rows || [];
  const pendingRows = rows.filter((r) => r.status !== 'DA_CHOT');
  const failing = rows.filter((r) => r.status === 'SE_TRU');
  const toPenalize = failing.filter((r) => chosen.has(r.employee_id));
  const beforeDeadline = data?.base_deadline && new Date() < new Date(data.base_deadline);
  const lb = data?.leaderboard;
  const winners = lb?.winners || [];
  // lb = null: máy chủ không đọc được bảng xếp hạng — vẫn cho chốt (máy chủ tự xét lại khi chốt).
  const lbPending = !lb || !lb.is_locked;
  const canClose = !!data && (pendingRows.length > 0 || lbPending);

  const toggle = (id) => {
    setChosen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const apply = async () => {
    setApplying(true);
    try {
      const res = await shiftPreferenceAPI.applyTltPenalty({
        branch_id: branchId,
        week_start: data.week_start,
        employee_ids: toPenalize.map((r) => r.employee_id),
      });
      const r = res?.week_label ? res : res?.data;
      const lbText = r?.leaderboard?.already_locked
        ? 'bảng xếp hạng đã chốt từ trước'
        : `bảng xếp hạng: cộng thưởng ${r?.leaderboard?.winners?.length ?? 0} NV`;
      const penText = r?.penalties
        ? `trừ TLT ${r.penalties.penalized} NV, bỏ qua ${r.penalties.skipped_by_admin}` +
          (r.penalties.errors ? `, ${r.penalties.errors} lỗi` : '')
        : 'chi nhánh đang tắt trừ TLT';
      toast.success(`Đã chốt tuần ${r?.week_label}: ${lbText}; ${penText}`);
      setConfirming(false);
      loadData();
      if (onClosed) onClosed(r);
    } catch (err) {
      toast.error(err?.message || 'Không chốt được tuần');
    } finally {
      setApplying(false);
    }
  };

  return (
    <>
      {data && (
        <Row className="mb-3 g-3">
          <Col
            xs={6}
            md={3}
          >
            <Card className="border-0 shadow-sm">
              <Card.Body className="text-center">
                <div className="fs-3 fw-bold text-danger">{data.summary.se_tru}</div>
                <div className="text-muted small">Không đạt (chưa chốt)</div>
              </Card.Body>
            </Card>
          </Col>
          <Col
            xs={6}
            md={3}
          >
            <Card className="border-0 shadow-sm">
              <Card.Body className="text-center">
                <div className="fs-3 fw-bold text-success">{data.summary.dat}</div>
                <div className="text-muted small">Đạt</div>
              </Card.Body>
            </Card>
          </Col>
          <Col
            xs={6}
            md={3}
          >
            <Card className="border-0 shadow-sm">
              <Card.Body className="text-center">
                <div className="fs-3 fw-bold text-info">{data.summary.mien}</div>
                <div className="text-muted small">Được miễn</div>
              </Card.Body>
            </Card>
          </Col>
          <Col
            xs={6}
            md={3}
          >
            <Card className="border-0 shadow-sm">
              <Card.Body className="text-center">
                <div className="fs-3 fw-bold">{data.summary.da_chot}</div>
                <div className="text-muted small">Đã chốt</div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {lb && (
        <Card className="border-0 shadow-sm mb-3">
          <Card.Body className="py-2 small d-flex flex-wrap gap-2 align-items-center">
            <span className="fw-semibold">🏆 Bảng xếp hạng đóng góp ca:</span>
            {lb.is_locked ? (
              <Badge bg="secondary">Đã chốt</Badge>
            ) : (
              <Badge
                bg="warning"
                text="dark"
              >
                Chưa chốt
              </Badge>
            )}
            {winners.length ? (
              winners.map((w) => (
                <span key={w.employee_id}>
                  Hạng {w.rank} <b>{w.employee_name}</b> +{w.reward_pct}% TLT
                </span>
              ))
            ) : (
              <span className="text-muted">không có ai trong nhóm được thưởng</span>
            )}
          </Card.Body>
        </Card>
      )}

      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-white d-flex align-items-center gap-3 flex-wrap py-3">
          <Form.Select
            size="sm"
            style={{ width: 'auto' }}
            value={weekStart || ''}
            onChange={(e) => setWeekStart(e.target.value || null)}
            disabled={!weekOptions.length}
          >
            {weekOptions.map((o) => (
              <option
                key={o.label}
                value={o.value}
              >
                {o.label}
              </option>
            ))}
          </Form.Select>
          {data && (
            <span className="small text-muted">
              Tuần <b>{data.week_label}</b> · hạn chốt {formatVn(data.base_deadline)} · duyệt ca lần cuối{' '}
              {formatVn(data.last_shift_approval_at)} · mức trừ {data.penalty_pct}%
            </span>
          )}
          <Button
            variant="outline-secondary"
            size="sm"
            className="ms-auto"
            onClick={loadData}
            disabled={loading}
          >
            {loading ? (
              <Spinner
                size="sm"
                animation="border"
              />
            ) : (
              'Làm mới'
            )}
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={() => setConfirming(true)}
            disabled={loading || !canClose}
          >
            {canClose
              ? `Chốt tuần (thưởng ${lbPending ? winners.length : 0} · trừ ${data?.penalty_enabled ? toPenalize.length : 0} NV)`
              : 'Tuần này đã chốt'}
          </Button>
        </Card.Header>
        <Card.Body className="p-0">
          {data && !data.penalty_enabled && (
            <Alert
              variant="warning"
              className="m-3"
            >
              Chi nhánh đang tắt trừ TLT đăng ký ca trong Cấu hình (shift_preference_penalty_enabled) — bấm Chốt tuần
              chỉ chốt bảng xếp hạng + cộng thưởng, không trừ ai.
            </Alert>
          )}
          {loading ? (
            <div className="text-center py-5">
              <Spinner
                animation="border"
                variant="primary"
              />
            </div>
          ) : !rows.length ? (
            <Alert
              variant="info"
              className="m-3"
            >
              Không có nhân viên nào trong diện xét.
            </Alert>
          ) : (
            <div className="table-responsive">
              <Table
                hover
                className="mb-0 align-middle"
              >
                <thead className="table-light">
                  <tr>
                    <th className="text-center">Trừ</th>
                    <th>Nhân viên</th>
                    <th>Điều kiện</th>
                    <th className="text-center">Trạng thái</th>
                    <th>Ghi chú</th>
                    <th className="text-center">TLT hiện tại</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const p = row.penalty;
                    return (
                      <tr
                        key={row.employee_id}
                        className={row.status === 'SE_TRU' ? 'table-danger bg-opacity-25' : ''}
                      >
                        <td className="text-center">
                          {row.status === 'SE_TRU' ? (
                            <Form.Check
                              type="checkbox"
                              checked={chosen.has(row.employee_id)}
                              onChange={() => toggle(row.employee_id)}
                            />
                          ) : null}
                        </td>
                        <td>
                          <div className="fw-semibold">{row.full_name}</div>
                          {!row.telegram_linked && <div className="text-muted small">Chưa liên kết Telegram</div>}
                        </td>
                        <td>
                          <Metrics m={row.metrics} />
                        </td>
                        <td className="text-center">
                          <StatusCell row={row} />
                        </td>
                        <td
                          className="small"
                          style={{ maxWidth: 320 }}
                        >
                          {row.fail_reasons?.length ? (
                            <span className="text-danger">{row.fail_reasons.join('; ')}</span>
                          ) : p ? (
                            <>
                              <div>{p.reason}</div>
                              <div className="text-muted">
                                {formatVn(p.created_at)}
                                {p.applied_by_name ? ` · ${p.applied_by_name} bấm` : ' · cron cũ tự trừ'}
                              </div>
                              {p.refunded_at && (
                                <div className="text-success">
                                  Hoàn {formatVn(p.refunded_at)} · {p.refunded_by_name}
                                  {p.refund_reason ? ` · ${p.refund_reason}` : ''}
                                </div>
                              )}
                            </>
                          ) : null}
                        </td>
                        <td className="text-center">{row.current_rate}%</td>
                        <td className="text-end">
                          {p?.penalized && !p.refunded_at && (
                            <Button
                              variant="outline-success"
                              size="sm"
                              onClick={() => setRefundRow(row)}
                            >
                              Hoàn TLT
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal
        show={confirming}
        onHide={() => setConfirming(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Chốt tuần {data?.week_label}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {beforeDeadline && (
            <Alert
              variant="warning"
              className="small"
            >
              Chưa tới hạn chốt ({formatVn(data?.base_deadline)}) — nhân viên vẫn còn có thể đăng ký thêm ca.
            </Alert>
          )}
          <p className="mb-1 fw-semibold">1. Bảng xếp hạng đóng góp ca</p>
          {!lb ? (
            <p className="small text-muted">
              Không đọc được bảng xếp hạng lúc xem trước — bấm chốt vẫn chốt và cộng thưởng theo dữ liệu mới nhất.
            </p>
          ) : !lbPending ? (
            <p className="small text-muted">Đã chốt từ trước — không cộng thưởng lại.</p>
          ) : winners.length ? (
            <ul className="small">
              {winners.map((w) => (
                <li key={w.employee_id}>
                  Hạng {w.rank} — {w.employee_name}: cộng {w.reward_pct}% TLT
                </li>
              ))}
            </ul>
          ) : (
            <p className="small text-muted">Chốt bảng xếp hạng, không có ai được thưởng.</p>
          )}
          <p className="mb-1 fw-semibold">2. Trừ TLT điều kiện đăng ký ca</p>
          {!data?.penalty_enabled ? (
            <p className="small text-muted">Chi nhánh đang tắt trừ TLT — bỏ qua.</p>
          ) : toPenalize.length ? (
            <>
              <p className="mb-2">
                Trừ <b>{data?.penalty_pct}%</b> TLT của <b>{toPenalize.length}</b> nhân viên (có gửi Telegram):
              </p>
              <ul className="small">
                {toPenalize.map((r) => (
                  <li key={r.employee_id}>
                    {r.full_name} — {r.fail_reasons.join('; ')}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="small">Không trừ ai — cả chi nhánh được chốt “không trừ”.</p>
          )}
          {failing.length > toPenalize.length && (
            <p className="small text-muted mb-0">
              {failing.length - toPenalize.length} nhân viên không đạt nhưng bỏ tích sẽ được chốt không trừ.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setConfirming(false)}
            disabled={applying}
          >
            Huỷ
          </Button>
          <Button
            variant="danger"
            onClick={apply}
            disabled={applying}
          >
            {applying ? (
              <Spinner
                size="sm"
                animation="border"
              />
            ) : (
              'Xác nhận chốt tuần'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <RefundModal
        key={refundRow?.penalty?.id || 'none'}
        row={refundRow}
        onHide={() => setRefundRow(null)}
        onDone={() => {
          setRefundRow(null);
          loadData();
        }}
      />
    </>
  );
}

/** Hộp "Chốt tuần" mở từ màn Quản lý ca làm việc. */
export function WeekCloseModal({ show, onHide, branchId, weekStart, onClosed }) {
  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      scrollable
    >
      <Modal.Header closeButton>
        <Modal.Title>Chốt tuần — thưởng bảng xếp hạng + trừ TLT đăng ký ca</Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-light">
        {show && (
          <WeekClosePanel
            branchId={branchId}
            initialWeekStart={weekStart}
            onClosed={onClosed}
          />
        )}
      </Modal.Body>
    </Modal>
  );
}
